<script setup lang="ts">
import { computed, useAttrs } from '#imports';
import { NuxtLink } from '#components';

type ButtonColor = 'default' | 'black' | 'green';
type ButtonVariation = 'icon';

type Props = {
	tag?: 'button' | 'a';
	color?: ButtonColor;
	variations?: Array<ButtonVariation>;
	disabled?: boolean;
};

const attrs = useAttrs();

const props = withDefaults(defineProps<Props>(), {
	tag: 'button',
	color: 'default',
	variations: () => [],
	disabled: false,
});

const component = computed(() => {
	return Object.keys(attrs).includes('to') ? NuxtLink : props.tag;
});

const computedClass = computed(() => {
	const modificators = props.variations.map((variation) => {
		return `v-button--${variation}`;
	});

	const classes = {
		[`v-button--${props.color}`]: props.color,
		'v-button--disabled': props.disabled,
	};

	return [classes, ...modificators];
});
</script>

<template>
	<component
		:is="component"
		class="v-button"
		:class="computedClass"
	>
		<span v-if="$slots.prefix" class="v-button__prefix">
			<slot name="prefix" />
		</span>

		<span class="v-button__content">
			<slot />
		</span>

		<span v-if="$slots.sufix" class="v-button__sufix">
			<slot name="sufix" />
		</span>
	</component>
</template>

<style lang="scss" scoped>
.v-button {
	$size: 16px;

	@include text-small;

	display: flex;
	align-items: center;

	height: rem(38px);
	padding: rem($gap-mini) rem(12px);

	cursor: pointer;
	text-decoration: none;
	transition:
		color $transition-function $transition-duration,
		border $transition-function $transition-duration,
		background-color $transition-function $transition-duration;
	text-align: center;

	border: rem(1px) solid;
	border-radius: rem($radius-secondary);
	background-color: transparent;

	@media #{$screen-laptop} {
		@include text-mini;
	}

	&--disabled {
		user-select: none;
		pointer-events: none;
		cursor: not-allowed;

		color: $dove-gray-1 !important;
		border-color: $silver !important;
		background-color: $silver !important;
	}

	&--default {
		color: $black;
		border-color: $black;

		@include hover {
			background-color: $white;
		}
	}

	&--black {
		color: $white;
		border-color: $black;
		background-color: $black;

		@include hover {
			border-color: $dove-gray;
			background-color: $dove-gray;
		}
	}

	&--green {
		color: #39db7f;
		border-color: #39db7f;
		background-color: rgba(#39db7f, .25);
	}

	&--icon & {
		&__content {
			width: rem($size);
			height: rem($size);
		}
	}

	&__prefix {
		flex-shrink: 0;

		width: rem($size);
		height: rem($size);
		margin-right: rem(12px);
	}

	&__content {
		flex-grow: 1;
	}

	&__sufix {
		flex-shrink: 0;

		width: rem($size);
		height: rem($size);
		margin-left: rem(12px);
	}
}
</style>
